import { Sentry } from './sentry';

/**
 * Performance monitoring utilities for tracking page load times and Web Vitals.
 * Integrates with Sentry's performance monitoring to ensure the app stays fast.
 */

// Web Vitals metric types
export type MetricName = 'CLS' | 'FID' | 'LCP' | 'FCP' | 'TTFB' | 'INP';

export interface PerformanceMetric {
  name: MetricName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

// Thresholds for Web Vitals (based on web.dev recommendations)
const METRIC_THRESHOLDS = {
  CLS: { good: 0.1, poor: 0.25 },
  FID: { good: 100, poor: 300 },
  LCP: { good: 2500, poor: 4000 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
} as const;

/**
 * Gets the rating for a metric value based on thresholds
 */
function getRating(name: MetricName, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = METRIC_THRESHOLDS[name];
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Reports a Web Vitals metric to Sentry
 */
function reportMetricToSentry(metric: PerformanceMetric) {
  // Only report to Sentry if it's initialized
  if (!Sentry) return;

  // Send metric as a measurement to Sentry
  Sentry.setMeasurement(metric.name, metric.value, 'millisecond');

  // For poor-performing metrics, add a breadcrumb
  if (metric.rating === 'poor') {
    Sentry.addBreadcrumb({
      category: 'performance',
      message: `Poor ${metric.name}: ${metric.value}`,
      level: 'warning',
      data: {
        metric: metric.name,
        value: metric.value,
        rating: metric.rating,
      },
    });
  }
}

/**
 * Observes and reports Web Vitals metrics using the Performance Observer API
 */
export function trackWebVitals() {
  if (typeof window === 'undefined' || !window.PerformanceObserver) {
    return;
  }

  // Track Largest Contentful Paint (LCP)
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };
      const value = lastEntry.renderTime || lastEntry.loadTime || 0;

      const metric: PerformanceMetric = {
        name: 'LCP',
        value,
        rating: getRating('LCP', value),
        delta: value,
        id: `lcp-${Date.now()}`,
      };

      reportMetricToSentry(metric);
    });

    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {
    // Observer not supported
  }

  // Track First Input Delay (FID)
  try {
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const fidEntry = entry as PerformanceEventTiming;
        const value = fidEntry.processingStart - fidEntry.startTime;

        const metric: PerformanceMetric = {
          name: 'FID',
          value,
          rating: getRating('FID', value),
          delta: value,
          id: `fid-${Date.now()}`,
        };

        reportMetricToSentry(metric);
      });
    });

    fidObserver.observe({ type: 'first-input', buffered: true });
  } catch (e) {
    // Observer not supported
  }

  // Track Cumulative Layout Shift (CLS)
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const layoutShiftEntry = entry as LayoutShift;
        if (!layoutShiftEntry.hadRecentInput) {
          clsValue += layoutShiftEntry.value;
        }
      });

      const metric: PerformanceMetric = {
        name: 'CLS',
        value: clsValue,
        rating: getRating('CLS', clsValue),
        delta: clsValue,
        id: `cls-${Date.now()}`,
      };

      reportMetricToSentry(metric);
    });

    clsObserver.observe({ type: 'layout-shift', buffered: true });
  } catch (e) {
    // Observer not supported
  }

  // Track First Contentful Paint (FCP)
  try {
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.name === 'first-contentful-paint') {
          const value = entry.startTime;

          const metric: PerformanceMetric = {
            name: 'FCP',
            value,
            rating: getRating('FCP', value),
            delta: value,
            id: `fcp-${Date.now()}`,
          };

          reportMetricToSentry(metric);
        }
      });
    });

    fcpObserver.observe({ type: 'paint', buffered: true });
  } catch (e) {
    // Observer not supported
  }
}

/**
 * Marks the start of a custom performance measurement
 */
export function startMeasure(name: string) {
  if (typeof window === 'undefined' || !window.performance) return;

  try {
    performance.mark(`${name}-start`);
  } catch (e) {
    // Performance API not available
  }
}

/**
 * Marks the end of a custom performance measurement and reports it
 */
export function endMeasure(name: string) {
  if (typeof window === 'undefined' || !window.performance) return;

  try {
    const endMark = `${name}-end`;
    const startMark = `${name}-start`;

    performance.mark(endMark);
    performance.measure(name, startMark, endMark);

    const measures = performance.getEntriesByName(name, 'measure');
    if (measures.length > 0) {
      const duration = measures[measures.length - 1].duration;

      // Report to Sentry
      if (Sentry) {
        Sentry.setMeasurement(name, duration, 'millisecond');
      }
    }

    // Clean up marks
    performance.clearMarks(startMark);
    performance.clearMarks(endMark);
    performance.clearMeasures(name);
  } catch (e) {
    // Performance API not available
  }
}

/**
 * Reports the current page load performance metrics
 */
export function reportPageLoad() {
  if (typeof window === 'undefined' || !window.performance) return;

  // Wait for load event to ensure all timing data is available
  if (document.readyState !== 'complete') {
    window.addEventListener('load', reportPageLoad, { once: true });
    return;
  }

  try {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    if (navigation) {
      // Time to First Byte
      const ttfb = navigation.responseStart - navigation.requestStart;
      const ttfbMetric: PerformanceMetric = {
        name: 'TTFB',
        value: ttfb,
        rating: getRating('TTFB', ttfb),
        delta: ttfb,
        id: `ttfb-${Date.now()}`,
      };
      reportMetricToSentry(ttfbMetric);

      // Report additional timing metrics to Sentry
      if (Sentry) {
        Sentry.setMeasurement('page-load', navigation.loadEventEnd - navigation.fetchStart, 'millisecond');
        Sentry.setMeasurement('dom-interactive', navigation.domInteractive - navigation.fetchStart, 'millisecond');
        Sentry.setMeasurement('dom-content-loaded', navigation.domContentLoadedEventEnd - navigation.fetchStart, 'millisecond');
      }
    }
  } catch (e) {
    // Performance API not available or navigation timing not supported
  }
}

/**
 * Initialize performance monitoring
 * Call this in your app entry point to start tracking performance metrics
 */
export function initPerformanceMonitoring() {
  if (typeof window === 'undefined') return;

  // Track Web Vitals
  trackWebVitals();

  // Report page load metrics
  reportPageLoad();
}
